import React from "react";
import Image from "components/image";
import { useDispatch, useSelector } from "react-redux";
import { ReactComponent as Tick } from "assets/svgs/tick.svg";
import { getRemainingProductsAndTags } from "utils/filters";
import clsx from "clsx/lite";
import { toggleFilter, toggleBundleFilter } from "slices/filters";

const FilterData = ({ data }) => {
	const dispatch = useDispatch();
	const { collections, tags, bundles } = useSelector(state => state.filters);
	const { remainingProducts, remainingTags } = getRemainingProductsAndTags(collections);

	const toggleTag = (tag) => {
		if (!remainingTags.includes(tag)) return;
		dispatch(toggleFilter(tag));
	};

	const toggleBundle = (bundle) => {
		// check if any of the products in the bundle are available
		const availableBundleProducts = bundle.products.some(bp => remainingProducts.includes(bp.id));
		if (!availableBundleProducts) return;
		dispatch(toggleBundleFilter(bundle));
	};

	const BundlesFilters = ({ filters }) => (
		<div className="my-10">
			<p className="text-sm font-bold leading-3 my-3 uppercase">{filters.name}</p>
			<div className="flex flex-wrap">
				{filters.data?.map((bundle) => (
					<button
						key={bundle.id}
						className={clsx("filter-label !p-1 !pr-3", bundles[bundle.title]?.isActive && "border-[black]",
							!bundle.products.some(bp => remainingProducts.includes(bp.id)) && "pointer-events-none text-gray-400"
						)}
						onClick={() => toggleBundle(bundle)}>
						<Image className="size-10 rounded-full" image={bundle.image} />
						<span>{bundle.title}</span>
						<span>{bundles[bundle.title]?.isActive && <Tick />}</span>
					</button>
				))}
			</div>
		</div>
	);

	const ButtonFilters = ({ filters }) => (
		<div className="my-10">
			<p className="text-sm font-bold leading-3 my-3 uppercase">{filters.name}</p>
			<div className="flex flex-wrap flex-row">
				{filters.data?.map((e) => (
					<button
						key={e.tag}
						className={clsx("filter-label", tags[e.tag].isActive && "border-[black]",
							!remainingTags.includes(e.tag) && "pointer-events-none text-gray-400"
						)}
						onClick={() => toggleTag(e.tag)}>
						<span>{e.displayName}</span>
						<span>{tags[e.tag].isActive && <Tick role="presentation" aria-hidden="true" />}</span>
					</button>
				))}
			</div>
			{filters.description &&
				<div className="mt-2 italic text-small text-gray-400">
					{filters.description}
				</div>}
		</div>
	);

	const CheckboxFilters = ({ filters }) => (
		<div className="my-10">
			<p className="text-sm font-bold leading-3 my-3 uppercase">{filters.name}</p>
			<div className="flex flex-wrap">
				{filters.data?.map((e) => (
					<div key={e.tag} className="w-1/2 cursor-pointer">
						<label className="flex items-center cursor-pointer">
							<input
								checked={tags[e.tag].isActive}
								disabled={!remainingTags.includes(e.tag)}
								onChange={() => toggleTag(e.tag)}
								type="checkbox" value={e.tag}
								className="checkbox-label cursor-pointer"></input>
							<span className={
								clsx("mt-1 cursor-pointer",
									!remainingTags.includes(e.tag) && "pointer-events-none text-gray-400")}>
								{e.displayName}
							</span>
						</label>
					</div>
				))}
				{filters.description && <span className="mt-2 italic text-small text-gray-400"> {filters.description}</span>}
			</div>
		</div>
	);

	const IconFilters = ({ filters }) => (
		<div className="my-10">
			<p className="text-sm font-bold my-3 leading-3 uppercase">{filters.name}</p>
			<div className="flex gap-x-5 flex-wrap">
				{filters.data?.map((e) => (
					<button
						key={e.tag}
						className={clsx("cursor-pointer text-center",
							!remainingTags.includes(e.tag) && "pointer-events-none text-gray-400")}
						onClick={() => toggleTag(e.tag)}>
						<div className={clsx(
							"bg-[#F5F5F5] size-20 rounded-full flex items-center justify-center",
							tags[e.tag].isActive ? "invert" : "invert-0"
						)}>
							<Image
								className={clsx("size-10", !remainingTags.includes(e.tag) && "opacity-50")}
								image={e.icon?.asset} alt={e.icon?.alt} />
						</div>
						<span className="mt-3 cursor-pointer">{e.displayName}</span>
					</button>
				))}
			</div>
		</div>
	);

	return data?.map((filters) => {
		switch (filters._type) {
			case "filter":
				return <ButtonFilters filters={filters} key={filters._key} />;
			case "checkbox":
				return <CheckboxFilters filters={filters} key={filters._key} />;
			case "iconFilter":
				return <IconFilters filters={filters} key={filters._key} />;
			case "bundlesFilter":
				return <BundlesFilters filters={filters} key={filters._key} />;
			default:
				return null;
		}
	});
};

export default FilterData;
